import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, ArrowRight } from 'lucide-react';

type UnauthorizedLocationState = {
  message?: string;
};

/** `reason` values from admin-only pages → the message shown when the redirect carries none. */
const ADMIN_ONLY_DENIALS = new Map<string, string>([
  ['campaign', 'Campaign management is limited to administrators.'],
  ['cdmo-import', 'Bulk uploading CDMO profiles is limited to administrators.'],
]);

const UnauthorizedAccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const stateMessage = (location.state as UnauthorizedLocationState | null)?.message;

  // Admin-only pages send non-admins here with their own reason; they share the wording style and
  // the way out (Find CDMOs), and never get the conversation text below.
  const adminOnlyFallback = reason ? ADMIN_ONLY_DENIALS.get(reason) : undefined;
  const isAdminOnlyDenial = adminOnlyFallback !== undefined;
  const errorMessage =
    stateMessage ||
    searchParams.get('message') ||
    adminOnlyFallback ||
    'You are not authorized to view this conversation.';
  const userRole = searchParams.get('role') || 'startup';

  const handleRedirect = () => {
    if (isAdminOnlyDenial) {
      navigate('/matchmaking', { replace: true });
      return;
    }
    const dashboardPath = userRole === 'cdmo' ? '/cdmo-dashboard' : '/startup-dashboard';
    navigate(dashboardPath, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
        className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
        >
          <Shield className="w-10 h-10 text-white" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-charcoal mb-4"
        >
          Access Not Authorized
        </motion.h1>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <div className="flex items-center justify-center mb-3">
            <AlertCircle className="w-5 h-5 text-orange-500 mr-2" />
            <span className="text-sm font-medium text-orange-600">Security Notice</span>
          </div>
          <p className="text-slate-gray text-base leading-relaxed">
            {errorMessage}
          </p>
          {!isAdminOnlyDenial ? (
            <p className="text-slate-gray text-sm mt-3">
              You can only access conversations where you are a participant. Please return to your
              dashboard to view your authorized conversations.
            </p>
          ) : null}
        </motion.div>

        {/* Action Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleRedirect}
          className="w-full px-6 py-3 bg-gradient-to-r from-electric-blue to-deep-indigo text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center group"
        >
          <span>{isAdminOnlyDenial ? 'Go to Find CDMOs' : `Go to ${userRole === 'cdmo' ? 'CDMO' : 'Startup'} Dashboard`}</span>
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </motion.div>
    </div>
  );
};

export default UnauthorizedAccess;
